import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, apiRequest } from "../../shared/api/client";
import { cashPlansSchema } from "../../../shared/account-finance/cash-plan-contract";
import type { CashAvailability, CashPlan, CashPlans } from "../../../shared/account-finance/cash-plan-contract";
import { cancelCashPlan, editCashPlan, restoreCashPlan } from "../../../shared/account-finance/cash-plan-lifecycle";
import type { CashPlanEditScope } from "../../../shared/account-finance/cash-plan-lifecycle";
import type { WorkbookDashboard } from "../../../shared/account-finance/finance-workbook-contract";

export function useCashPlansWorkspace(enabled: boolean, book: WorkbookDashboard | null, onSaved: (data: CashPlans) => void) {
  const [data, setData] = useState<CashPlans | null>(null);
  const [error, setError] = useState<string | null>(null), [success, setSuccess] = useState<string | null>(null);
  const [conflict, setConflict] = useState(false), [busy, setBusy] = useState(false), [loading, setLoading] = useState(false);
  const pending = useRef(false), readPending = useRef(false), request = useRef(0);
  const mounted = useRef(true), savedCallback = useRef(onSaved);
  const controller = useRef<AbortController | null>(null);
  const company = book?.company;
  useEffect(() => { savedCallback.current = onSaved; }, [onSaved]);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; controller.current?.abort(); }; }, []);

  const reload = useCallback(async (notify = true) => {
    if (!company || pending.current) return false;
    controller.current?.abort();
    const abort = new AbortController(); controller.current = abort;
    const id = ++request.current; readPending.current = true; setLoading(true); setError(null);
    try {
      const raw = await apiRequest<unknown>("/api/account-finance/workbook/plans", {
        signal: AbortSignal.any([abort.signal, AbortSignal.timeout(15000)]), cache: "no-store",
      });
      const plans = cashPlansSchema.parse(raw);
      if (plans.company !== company) throw new Error("Plan şirketi eşleşmiyor.");
      if (!mounted.current || abort.signal.aborted || id !== request.current) return false;
      setData(plans); setConflict(false);
      savedCallback.current(plans);
      if (notify) setSuccess("Güncel kayıtlar yüklendi. Formdaki değişikliklerinizi kontrol ederek kaydedebilirsiniz.");
      return true;
    } catch (failure) {
      if (!abort.signal.aborted && mounted.current && id === request.current)
        setError(failure instanceof Error ? failure.message : "Planlar yüklenemedi.");
      return false;
    } finally {
      if (mounted.current && id === request.current) { readPending.current = false; setLoading(false); }
    }
  }, [company]);

  useEffect(() => {
    if (!enabled || !company) return;
    setData(previous => previous?.company === company ? previous : null);
    void reload(false);
    return () => { controller.current?.abort(); request.current += 1; readPending.current = false; };
  }, [enabled, company, reload]);

  const save = async (next: CashPlans, message: string) => {
    if (!book || pending.current || readPending.current) return false;
    pending.current = true; setBusy(true); setError(null); setSuccess(null);
    const id = request.current;
    try {
      const saved = cashPlansSchema.parse(await apiRequest<unknown>(`/api/account-finance/workbook/plans?date=${book.date}`, {
        method: "PUT", body: JSON.stringify(next), signal: AbortSignal.timeout(15000),
      }));
      if (saved.company !== book.company) throw new Error("Plan şirketi eşleşmiyor.");
      if (mounted.current && id === request.current) { setData(saved); setConflict(false); setSuccess(message); }
      if (mounted.current && id === request.current) savedCallback.current(saved);
      return true;
    } catch (failure) {
      if (mounted.current && id === request.current) {
        setConflict(failure instanceof ApiError && ["CASH_PLAN_CONFLICT", "HTTP_409"].includes(failure.code));
        setError(failure instanceof Error ? failure.message : "Plan kaydedilemedi. Alanlarınız korunuyor.");
      }
      return false;
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const operate = async (change: (current: CashPlans) => CashPlans, message: string) => {
    if (!data || data.company !== company) { setError("Önce güncel planları yükleyin."); return false; }
    try { return await save(cashPlansSchema.parse(change(data)), message); }
    catch (failure) { setError(failure instanceof Error ? failure.message : "Plan değişikliği geçersiz."); return false; }
  };
  return {
    data, error, success, conflict, busy, loading, reload: () => reload(),
    clearFeedback: () => { setError(null); setSuccess(null); },
    add: (plan: CashPlan) => operate(current => ({ ...current, plans: [...current.plans, plan] }), "Plan kaydedildi."),
    edit: (plan: CashPlan, scope: CashPlanEditScope = "all", occurrenceDate = plan.startDate) =>
      operate(current => editCashPlan(current, plan, scope, occurrenceDate), "Plan değişikliği kaydedildi. Belge eşlemeleri korundu."),
    remove: (id: string, fromDate = book?.date ?? "") =>
      operate(current => cancelCashPlan(current, id, fromDate), "Plan bu tarihten itibaren arşivlendi. Geçmiş ve belge eşlemeleri korundu."),
    cancelOccurrence: (id: string, date: string) =>
      operate(current => cancelCashPlan(current, id, date, true), "Bu dönem iptal edildi. Belge eşlemeleri korundu."),
    restore: (id: string, date?: string) => operate(current => restoreCashPlan(current, id, date), "Plan yeniden etkinleştirildi."),
    saveAvailability: (entry: CashAvailability) => operate(current => ({ ...current,
      cashAvailability: [...(current.cashAvailability ?? []).filter(item => item.date !== entry.date || item.currency !== entry.currency), entry],
    }), "Kullanılabilir nakit beyanı kaydedildi."),
    removeAvailability: (date: string, currency: CashAvailability["currency"]) => operate(current => ({ ...current,
      cashAvailability: (current.cashAvailability ?? []).filter(item => item.date !== date || item.currency !== currency),
    }), "Nakit beyanı kaldırıldı; kullanılabilir nakit yeniden doğrulanmalı."),
    unbind: (planId: string, date: string, sourceSheet: string, sourceRow: string) => operate(current => ({ ...current,
      bindings: current.bindings.filter(item => item.planId !== planId || item.date !== date || item.sourceSheet !== sourceSheet || item.sourceRow !== sourceRow),
    }), "Belge eşlemesi kaldırıldı; tahmin tutarı yeniden hesaplandı."),
    bind: (planId: string, date: string, sourceSheet: string, sourceRow: string, amount: string) => {
      const sheet = book?.sheets.find(item => item.id === sourceSheet);
      if (!sheet) { setError("Kaynak belge yeniden yüklenmeli."); return Promise.resolve(false); }
      return operate(current => ({ ...current, bindings: [...current.bindings,
        { planId, date, sourceSheet, sourceRow, amount, observedAt: sheet.observedAt }],
      }), "Belge eşleştirildi. Bu işlem ödemenin yapıldığını doğrulamaz.");
    },
  };
}
